import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";

/**
 * Buyer-safe payout status for the "Automatic seller payout" countdown panel
 * on Orders → My purchases. The caller must be the buyer of the order.
 *
 * Never exposes Stripe identifiers, charge IDs, connected account IDs,
 * transfer IDs, or raw provider errors to the browser.
 */

export type BuyerAutoPayoutState =
  | "not_applicable"
  | "counting_down"
  | "processing"
  | "delivery_confirmed_processing"
  | "released"
  | "dispute_paused"
  | "delayed"
  | "cancelled";

export type BuyerAutoPayoutStatus = {
  state: BuyerAutoPayoutState;
  autoPayoutEligibleAt: string | null;
  msRemainingUntilEligible: number | null;
  hasActiveDispute: boolean;
  deliveredAt: string | null;
  deliveryConfirmedAt: string | null;
  payoutReleasedAt: string | null;
  payoutReleased: boolean;
  orderStatus: string;
};

export const getBuyerAutoPayoutStatus = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d) => z.object({ orderId: z.string().uuid() }).parse(d))
  .handler(async ({ data, context }): Promise<BuyerAutoPayoutStatus> => {
    const { supabase, userId } = context;

    const { data: order, error } = await supabase
      .from("orders")
      .select(
        "id,buyer_id,status,delivered_at,delivery_confirmed_at,has_active_dispute,stripe_transfer_id,payout_released_at",
      )
      .eq("id", data.orderId)
      .maybeSingle();
    if (error) throw new Error(error.message);
    if (!order) throw new Error("Order not found");
    if (order.buyer_id !== userId) throw new Error("Forbidden");

    const deliveredAt = order.delivered_at ? new Date(order.delivered_at) : null;
    const eligibleAt = deliveredAt
      ? new Date(deliveredAt.getTime() + 72 * 60 * 60 * 1000)
      : null;
    const msRemaining = eligibleAt ? eligibleAt.getTime() - Date.now() : null;
    const payoutReleased =
      !!order.payout_released_at || !!order.stripe_transfer_id || order.status === "released";

    let state: BuyerAutoPayoutState = "not_applicable";
    if (payoutReleased) {
      state = "released";
    } else if (order.status === "refunded" || order.status === "cancelled") {
      state = "cancelled";
    } else if (order.has_active_dispute) {
      state = "dispute_paused";
    } else if (order.status === "delivered" && deliveredAt) {
      if (order.delivery_confirmed_at) {
        state = "delivery_confirmed_processing";
      } else if (msRemaining !== null && msRemaining <= 0) {
        const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
        const { data: failEvent } = await supabaseAdmin
          .from("payment_audit_log")
          .select("created_at")
          .eq("order_id", order.id)
          .eq("event_type", "automatic_72h_payout_failed")
          .order("created_at", { ascending: false })
          .limit(1)
          .maybeSingle();
        const { data: succEvent } = await supabaseAdmin
          .from("payment_audit_log")
          .select("created_at")
          .eq("order_id", order.id)
          .eq("event_type", "automatic_72h_payout_succeeded")
          .order("created_at", { ascending: false })
          .limit(1)
          .maybeSingle();
        const failedAfter =
          failEvent &&
          (!succEvent ||
            new Date(failEvent.created_at).getTime() >
              new Date(succEvent.created_at).getTime());
        state = failedAfter ? "delayed" : "processing";
      } else {
        state = "counting_down";
      }
    }

    return {
      state,
      autoPayoutEligibleAt: eligibleAt?.toISOString() ?? null,
      msRemainingUntilEligible: msRemaining,
      hasActiveDispute: !!order.has_active_dispute,
      deliveredAt: order.delivered_at ?? null,
      deliveryConfirmedAt: order.delivery_confirmed_at ?? null,
      payoutReleasedAt: order.payout_released_at ?? null,
      payoutReleased,
      orderStatus: order.status,
    };
  });
